import React, { useEffect, useState } from "react";
import { SectionLabel, LiveDot } from "./ui";
import WhatsAppDemo from "./WhatsAppDemo";

const FLOWS = {
  whatsapp: {
    title: "WhatsApp Commerce Agent",
    sub: "Turns WhatsApp into your fastest sales rep — multilingual, 24/7",
    industry: "Distribution · Retail · B2B Commerce",
    isWhatsApp: true,
    kpi: [
      { k: "Order capture time", before: "12 min", after: "90 sec" },
      { k: "Monthly orders", before: "Baseline", after: "+62%" },
      { k: "Cost per chat", before: "$1.40", after: "$0.17" },
    ],
    steps: [],
  },
  ar: {
    title: "AR Collections Agent",
    sub: "Recovers receivables before they become bad debt",
    industry: "Consumer Goods · Distribution",
    kpi: [
      { k: "Day sales outstanding", before: "47 d", after: "29 d" },
      { k: "Recovery rate", before: "68%", after: "94%" },
      { k: "Collections team hours", before: "240/wk", after: "18/wk" },
    ],
    steps: [
      { role: "trigger", text: "ERP event: 312 invoices crossed 30d aging" },
      { role: "think",   text: "Ranking by recovery probability × invoice size" },
      { role: "tool",    text: "tool.call(whatsapp.send) — 47 personalized messages (Bangla/English)" },
      { role: "tool",    text: "tool.call(call.dial) — 11 retailers with high-value invoices" },
      { role: "result",  text: "21 retailers committed · 6 disputes escalated to humans" },
      { role: "act",     text: "Posted $284,200 in expected receipts to forecast" },
      { role: "learn",   text: "Updated policy: send-time 'Fri 11am' lifts response by 18%" },
    ],
  },
  supply: {
    title: "Supplier Onboarding Agent",
    sub: "Vets, verifies and contracts new suppliers without a buyer touching it",
    industry: "Procurement · Manufacturing",
    kpi: [
      { k: "Onboarding cycle", before: "21 days", after: "38 hours" },
      { k: "KYB pass rate", before: "61%", after: "97%" },
      { k: "Cost per supplier", before: "$420", after: "$28" },
    ],
    steps: [
      { role: "trigger", text: "New supplier intake form submitted" },
      { role: "tool",    text: "tool.call(kyb.verify) — registry + sanctions + UBO checks" },
      { role: "think",   text: "Categorizing by risk tier · matching to existing contracts" },
      { role: "tool",    text: "tool.call(docusign.send) — pre-filled MSA + NDA" },
      { role: "act",     text: "Pushed supplier record to ERP · enrolled in payment portal" },
      { role: "result",  text: "Supplier production-ready in 38h · 100% audit trail" },
      { role: "learn",   text: "Detected 3 new red-flag patterns → added to guardrails" },
    ],
  },
  cx: {
    title: "Customer Experience Agent",
    sub: "Resolves, retains and upsells across every channel — in 22 languages",
    industry: "Hospitality · Retail · Healthcare",
    kpi: [
      { k: "First-contact resolution", before: "44%", after: "89%" },
      { k: "CSAT", before: "3.6 / 5", after: "4.7 / 5" },
      { k: "Tickets / agent / day", before: "32", after: "Auto" },
    ],
    steps: [
      { role: "trigger", text: "Inbound: 'My room AC isn't cooling — checking out tomorrow.'" },
      { role: "think",   text: "Guest = Platinum loyalty · 3 prior stays · low complaint rate" },
      { role: "tool",    text: "tool.call(pms.lookup) — room 1402 has 2 past AC tickets" },
      { role: "act",     text: "Dispatched engineering · offered complimentary upgrade · 480pt credit" },
      { role: "result",  text: "Guest accepted upgrade · CSAT survey 5/5 · NPS promoter saved" },
      { role: "learn",   text: "Pattern noted: room 1402 AC. Filed maintenance work order." },
    ],
  },
  forecast: {
    title: "Demand Forecast Agent",
    sub: "Forecasts SKU-level demand and triggers replenishment automatically",
    industry: "FMCG · Electronics · Pharma",
    kpi: [
      { k: "Forecast accuracy", before: "71%", after: "94%" },
      { k: "Stockouts / mo", before: "184", after: "12" },
      { k: "Excess inventory", before: "$3.2M", after: "$680K" },
    ],
    steps: [
      { role: "trigger", text: "Nightly: 12,400 SKUs × 480 outlets = 5.9M forecast points" },
      { role: "think",   text: "Blending POS + weather + festival calendar + competitor pricing" },
      { role: "act",     text: "Detected demand spike: Eid in 11 days · cooking oil +340%" },
      { role: "tool",    text: "tool.call(erp.po_create) — 14 expedited POs · 3 backup suppliers" },
      { role: "result",  text: "Saved estimated $412K in lost sales · zero spoilage" },
      { role: "learn",   text: "Festival uplift model retrained · accuracy +3.2pp" },
    ],
  },
};

const RoleChip = ({ role }) => {
  const map = {
    trigger: { tag: "TRIGGER", chip: "bg-rose-400/10 text-rose-200" },
    think:   { tag: "THINK",   chip: "bg-cyan-300/10 text-cyan-200" },
    tool:    { tag: "TOOL",    chip: "bg-violet-400/10 text-violet-200" },
    act:     { tag: "ACT",     chip: "bg-blue-400/10 text-blue-200" },
    result:  { tag: "RESULT",  chip: "bg-emerald-400/10 text-emerald-200" },
    learn:   { tag: "LEARN",   chip: "bg-amber-300/10 text-amber-200" },
  };
  const c = map[role];
  return <div className={`shrink-0 rounded px-1.5 py-[2px] font-mono text-[9px] tracking-widest ${c.chip}`}>{c.tag}</div>;
};

const roleColor = (r) => ({
  result: "text-emerald-200", learn: "text-amber-200", think: "text-cyan-200",
  tool: "text-violet-200", act: "text-blue-200", trigger: "text-rose-200",
}[r] || "text-white");

export default function DemoTheater() {
  const flowKeys = Object.keys(FLOWS);
  const [active, setActive] = useState("whatsapp");
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(true);
  const flow = FLOWS[active];

  useEffect(() => { setStep(0); }, [active]);
  useEffect(() => {
    if (!playing || flow.isWhatsApp) return;
    if (step > flow.steps.length) {
      const t = setTimeout(() => setStep(0), 2200);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setStep(s => s + 1), 950);
    return () => clearTimeout(t);
  }, [step, playing, flow]);

  return (
    <section id="demo" className="relative py-24 lg:py-32 bg-gradient-to-b from-[#060c22] via-[#050a1f] to-[#050a1f]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid items-end gap-8 lg:grid-cols-[1fr,auto]">
          <div className="max-w-2xl">
            <SectionLabel tone="accent">Live demo theater</SectionLabel>
            <h2 className="mt-5 text-[36px] leading-[1.05] tracking-tight sm:text-[44px] lg:text-[52px]">
              <span className="text-white">Pick a workflow.</span>{" "}
              <span className="font-display italic grad-text">Watch the agent run it.</span>
            </h2>
            <p className="mt-5 max-w-xl text-[16px] leading-relaxed text-white/65">
              These are real agent flows from our deployments — anonymized. The same engine runs hundreds of variants in production today.
            </p>
          </div>
          <button onClick={() => setPlaying(p => !p)} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-white/70 hover:bg-white/[0.08]">
            {playing ? "⏸ pause demo" : "▶ play demo"}
          </button>
        </div>

        {/* Flow selector */}
        <div className="mt-10 grid grid-cols-2 gap-2 lg:grid-cols-5">
          {flowKeys.map(k => {
            const f = FLOWS[k];
            const isActive = k === active;
            const isWA = f.isWhatsApp;
            return (
              <button key={k} onClick={() => setActive(k)}
                className={`group rounded-2xl border p-4 text-left transition-all ${
                  isActive
                    ? (isWA ? "border-emerald-300/40 bg-emerald-300/[0.05] shadow-[0_0_0_1px_rgba(94,234,212,0.15)]"
                            : "border-cyan-300/40 bg-cyan-300/[0.05] shadow-[0_0_0_1px_rgba(94,234,212,0.15)]")
                    : "border-white/[0.08] bg-white/[0.025] hover:border-white/20"
                }`}>
                <div className={`font-mono text-[10px] uppercase tracking-[0.2em] ${isWA ? "text-emerald-300/80" : "text-white/40"}`}>
                  {isWA && <span className="mr-1">★</span>}{f.industry}
                </div>
                <div className={`mt-2 text-[15px] font-semibold ${isActive ? "text-white" : "text-white/85"}`}>{f.title}</div>
                <div className={`mt-1 text-[12px] ${isActive ? "text-white/70" : "text-white/45"}`}>{f.sub}</div>
              </button>
            );
          })}
        </div>

        {/* Theater */}
        <div className="mt-8 grid gap-6 lg:grid-cols-[1.3fr,1fr]">
          {flow.isWhatsApp ? (
            <WhatsAppDemo height={500} />
          ) : (
            <div className="rounded-2xl border border-white/[0.08] bg-gradient-to-b from-[#0b1535] to-[#070d24] overflow-hidden">
              <div className="flex items-center justify-between border-b border-white/[0.06] px-4 py-3">
                <div className="flex items-center gap-2 font-mono text-[11px] text-white/45">
                  <LiveDot /><span>agent_run · {active} · session_19f4</span>
                </div>
                <span className="font-mono text-[10px] text-white/35">{Math.min(step, flow.steps.length)}/{flow.steps.length}</span>
              </div>
              <div className="px-5 py-5 min-h-[380px] flex flex-col gap-3">
                {flow.steps.slice(0, step).map((s, i) => (
                  <div key={i} className="flex items-start gap-3 fadeup" style={{ animationDelay: `${i * 40}ms` }}>
                    <RoleChip role={s.role} />
                    <div className={`font-mono text-[12.5px] leading-[1.55] ${roleColor(s.role)}`}>{s.text}</div>
                  </div>
                ))}
                {step < flow.steps.length && (
                  <div className="flex items-center gap-2 font-mono text-[12px] text-white/35">
                    <span className="caret">▍</span><span>working…</span>
                  </div>
                )}
                {step >= flow.steps.length && (
                  <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-emerald-300/25 bg-emerald-300/[0.06] px-3 py-1.5 font-mono text-[11px] text-emerald-200 w-fit">
                    ✓ run complete · loop closed · learning saved
                  </div>
                )}
              </div>
            </div>
          )}

          {/* KPI panel */}
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-300/80">Before · after</div>
            <h3 className="mt-2 text-[20px] font-semibold text-white">{flow.title}</h3>
            <p className="mt-1 text-[13px] text-white/55">{flow.sub}</p>
            <div className="mt-6 space-y-4">
              {flow.kpi.map(k => (
                <div key={k.k} className="rounded-xl border border-white/[0.06] bg-[#070d24] p-4">
                  <div className="text-[12px] text-white/55">{k.k}</div>
                  <div className="mt-2 flex items-end justify-between gap-4">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/35">Before</div>
                      <div className="text-[18px] font-semibold text-white/70 nums line-through decoration-rose-400/50">{k.before}</div>
                    </div>
                    <svg className="h-4 w-6 text-cyan-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    <div className="text-right">
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-300/80">With agent</div>
                      <div className="text-[20px] font-semibold text-cyan-200 nums">{k.after}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <a href="#book" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white px-5 py-3 text-[14px] font-semibold text-[#051030] hover:-translate-y-0.5">
              Build this agent for us
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
